import { useUrlGenerator } from '@folklore/routes';
import {
    useMutation,
    useQuery,
    useQueryClient,
    keepPreviousData as previousPlaceholder,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { useLocation } from 'wouter';

import { useApi } from '../contexts/ApiContext';
import useSetUser from './useSetUser';

export default function useGame(gameId, opts = null) {
    const api = useApi();
    const { keepPreviousData = true } = opts || {};
    const {
        data: game = null,
        isFetched = false,
        ...queryResult
    } = useQuery<Game>({
        queryKey: ['game', gameId],
        queryFn: ({ queryKey: [, idParam], signal }) => api.games.findById(idParam, { signal }),
        suspense: false,
        placeholderData: keepPreviousData ? previousPlaceholder : undefined,
        ...opts,
    });

    return {
        game,
        isFetched,
        ...queryResult,
    };
}

export interface GameStateResponse {
    participation: Participation | null;
    participant: Participant;
    game: Game;
}

export function useUpdateGameStateResponse() {
    const url = useUrlGenerator();
    const queryClient = useQueryClient();
    const setUser = useSetUser();
    return useCallback(
        (response: GameStateResponse, location: string | null = null) => {
            const { participation = null, participant = null, game = null } = response;
            const updatedPage: { game?: Game; participation?: Participation } = {};
            if (game !== null) {
                queryClient.setQueryData(['game', game.id], game);
                updatedPage.game = game;
            }
            if (participant !== null) {
                setUser(participant);
            }
            if (participation !== null) {
                queryClient.setQueryData(['participation', participation.id], participation);
                queryClient.invalidateQueries({
                    queryKey: ['page', url('play')],
                });
                updatedPage.participation = participation;
            }

            [
                participation === null ? url('game', { game: game.id }) : null,
                participation !== null
                    ? url('participation', { participation: participation.id })
                    : null,
                location,
            ]
                .filter((page) => page !== null)
                .forEach((pageUrl) => {
                    const currentPage = queryClient.getQueryData(['page', pageUrl]) || null;
                    if (typeof currentPage === 'object' && currentPage !== null) {
                        queryClient.setQueryData(['page', pageUrl], {
                            ...currentPage,
                            ...updatedPage,
                        });
                    }
                });
        },
        [url, queryClient],
    );
}

export function useAnswerQuestion({
    game,
    participation = null,
}: {
    game: Game;
    participation?: Participation | null;
}) {
    const updateGameStateResponse = useUpdateGameStateResponse();
    const api = useApi();
    const [location] = useLocation();
    const { id: gameId } = game;
    const { id: participationId = null } = participation || {};
    const { mutate, mutateAsync, ...mutationResult } = useMutation<
        GameStateResponse,
        Error,
        { questionId: string; answer: any }
    >({
        mutationFn: ({ questionId, answer }) =>
            participationId !== null
                ? api.participations.answerQuestion(participationId, questionId, answer)
                : api.games.answerQuestion(gameId, questionId, answer),
        onSuccess: (response) => {
            updateGameStateResponse(response, location);
        },
    });

    return {
        answerQuestion: mutate,
        answerQuestionAsync: mutateAsync,
        ...mutationResult,
    };
}

export function useAnswerGame({
    game,
    participation = null,
}: {
    game: Game;
    participation?: Participation | null;
}) {
    const [location] = useLocation();
    const updateGameStateResponse = useUpdateGameStateResponse();
    const api = useApi();
    const { id: gameId } = game;
    const { id: participationId = null } = participation || {};
    const { mutate, mutateAsync, ...mutationResult } = useMutation<GameStateResponse, Error, any>({
        mutationFn: (answer) =>
            participationId !== null
                ? api.participations.answer(participationId, answer)
                : api.games.answer(gameId, answer),
        onSuccess: (response) => {
            updateGameStateResponse(response, location);
        },
    });

    return {
        useAnswerGame: mutate,
        useAnswerGameAsync: mutateAsync,
        ...mutationResult,
    };
}
